/// <amd-module name="Comments/Redux/ConnectedCommentItem" />
import { CommentReactionType, IComment } from "Comments/Common/Contracts";
import { CommentItem } from "Comments/Item/CommentItem";
import { IReactionTypeToInfoMap } from "Comments/Reactions/Contracts";
import { CommentsActions } from "Comments/Redux/CommentsActions";
import { CommentsReduxModule } from "Comments/Redux/CommentsReduxModule";
import { ArtifactKeyDelimiter, getCommentItemState, getCommentReactionInfoMap } from "Comments/Redux/CommentsSelectors";
import { IConnectedCommentItemProps } from "Comments/Redux/ConnectedCommentItem.Props";
import { ICommentsReduxAwareState } from "Comments/Redux/Contracts";
import * as React from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { VssComponent } from "VSS/Platform/Layout";
import { noop } from "VSSUI/Util";

interface ICommentItemAdapterStateProps {
    /**
     * Comment object (state may return null for deleted or undefined for missing).
     */
    comment: IComment | null | undefined;

    /**
     * True if comment is in edit mode.
     */
    isEditing: boolean;

    /**
     * True if viewer is readonly mode (i.e. write actions such as reactions will be disabled)
     */
    readonly: boolean;

    /**
     * True if calls to server to run pending reaction actions initiated
     */
    isPendingReactionActionUpdatesInitiated: boolean;

    /**
     * Dictionary of reaction types to its corresponding info.
     */
    reactionTypeToInfoMap: IReactionTypeToInfoMap;
}

type IProps = IConnectedCommentItemProps & ICommentItemAdapterStateProps & typeof Actions;
class CommentItemAdapter extends React.PureComponent<IProps> {
    constructor(props: IProps, context?: any) {
        super(props, context);

        const { artifactInfo } = this.props;
        const { artifactKind } = artifactInfo;
        if (artifactKind.indexOf(ArtifactKeyDelimiter) >= 0) {
            // unlikely, but in case Comments\Redux consumer use the delimiter in the artifact kind value
            throw new Error(`Comment artifact kind should not contain "${ArtifactKeyDelimiter}": ${artifactKind}`);
        }
    }

    public render(): JSX.Element {
        const { comment, onRenderDeleted, onRenderLoadingCommentComponent, onRenderEditor } = this.props;

        if (comment === undefined) {
            return onRenderLoadingCommentComponent ? onRenderLoadingCommentComponent() : <></>;
        }

        if (comment === null || comment.isDeleted) {
            return onRenderDeleted ? onRenderDeleted(comment) : <></>;
        }

        return (
            <CommentItem
                {...this.props}
                comment={comment}
                viewerClassName="comment-viewer"
                viewerContentClassName="comment-content"
                onStartEdit={this._onStartEdit}
                onCancelEdit={this._onCancelEdit}
                onConfirmDelete={this._onConfirmDelete}
                onUpdate={this._onUpdate}
                onRenderEditor={onRenderEditor}
                onAddReaction={this._onAddReaction}
                onDeleteReaction={this._onDeleteReaction}
                onGetRecentlyReactedUsers={this._onGetRecentlyReactedUsers}
            />
        );
    }

    public componentDidMount(): void {
        const {
            artifactInfo,
            comment,
            commentId,
            autoLoadComment,
            startGetItem,
            startEditItem,
            startDeleteItem,
            startEditOnMount = false,
            deleteOnMount = false
        } = this.props;
        if (deleteOnMount) {
            startDeleteItem(artifactInfo, commentId);
        } else {
            if (comment === undefined && autoLoadComment) {
                startGetItem(artifactInfo, commentId, startEditOnMount);
            } else {
                if (comment && startEditOnMount) {
                    startEditItem(artifactInfo, commentId);
                }

                this._runPendingReactionsActions();
            }
        }
    }

    public componentDidUpdate(prevProps: IProps): void {
        const { commentId, comment, onDeleteCompleted = noop, onAutoLoadCompleted = noop, onUpdateCompleted = noop } = this.props;

        // comment was marked as deleted and now there is a new comment reference
        if (prevProps.deleteOnMount && prevProps.comment !== comment) {
            onDeleteCompleted(commentId);
            return;
        }

        // comment wasn't in the store, auto load is enabled, and now we have a comment
        if (prevProps.comment === undefined && prevProps.autoLoadComment && comment) {
            onAutoLoadCompleted(comment);
            this._runPendingReactionsActions();
            return;
        }

        // both previous and current comment objects are valid and are now different
        if (prevProps.comment && comment && prevProps.comment !== comment) {
            if (!prevProps.comment.isDeleted && comment.isDeleted) {
                onDeleteCompleted(commentId); // specifically call onDelete when going from active to deleted
            } else {
                onUpdateCompleted(comment);
            }

            return;
        }
    }

    private _onStartEdit = (commentId: number): void => {
        const { artifactInfo, startEditItem, onStartEdit } = this.props;

        onStartEdit && onStartEdit(commentId);
        startEditItem(artifactInfo, commentId);
    };

    private _onCancelEdit = (commentId: number): void => {
        const { artifactInfo, cancelEditItem } = this.props;

        cancelEditItem(artifactInfo, commentId);
    };

    private _onConfirmDelete = (commentId: number, confirmed: boolean): void => {
        const { artifactInfo, startDeleteItem } = this.props;

        if (confirmed) {
            startDeleteItem(artifactInfo, commentId);
        }
    };

    private _onUpdate = (commentId: number, newValue: string): void => {
        const { artifactInfo, startUpdateItem } = this.props;

        startUpdateItem(artifactInfo, commentId, newValue);
    };

    private _onAddReaction = (commentId: number, reactionType: CommentReactionType, isAddedFromStatusBar: boolean): void => {
        const { artifactInfo, startUpdateReaction } = this.props;
        startUpdateReaction(artifactInfo, commentId, reactionType, true, true);
    };

    private _onDeleteReaction = (commentId: number, reactionType: CommentReactionType, isDeletedFromStatusBar: boolean): void => {
        const { artifactInfo, startUpdateReaction } = this.props;
        startUpdateReaction(artifactInfo, commentId, reactionType, false, true);
    };

    private _onGetRecentlyReactedUsers = (commentId: number, reactionType: CommentReactionType, top: number): void => {
        const { artifactInfo, startGetRecentlyReactedUsers } = this.props;
        startGetRecentlyReactedUsers(artifactInfo, commentId, reactionType, top);
    };

    private _runPendingReactionsActions(): void {
        const {
            artifactInfo,
            commentId,
            isReactionsEnabled,
            isPendingReactionActionUpdatesInitiated,
            pendingReactionActions,
            startUpdatePendingReactions
        } = this.props;

        if (isReactionsEnabled && !isPendingReactionActionUpdatesInitiated) {
            const pendingReactionActionsMap: { [reactionType: number]: boolean } = {};
            const pendingActions = pendingReactionActions ? pendingReactionActions.value : undefined;

            if (pendingActions) {
                pendingActions.forEach((pendingAction, reactionType) => {
                    if (pendingAction !== 0) {
                        pendingReactionActionsMap[reactionType] = pendingAction === 1 ? true : false;
                    }
                });
            }

            // Run this action even if pendingActions is empty so that the isPendingReactionActionUpdatesInitiated
            // value is updated in the state
            startUpdatePendingReactions(artifactInfo, commentId, pendingReactionActionsMap);
        }
    }
}

function mapStateToProps(state: ICommentsReduxAwareState, props: IConnectedCommentItemProps): ICommentItemAdapterStateProps {
    const { artifactInfo, commentId, readonly = false, pendingReactionActions } = props;
    const commentItemState = getCommentItemState(state, artifactInfo, commentId);
    const comment = commentItemState && commentItemState.comment;
    const isPendingReactionActionUpdatesInitiated = !!(commentItemState && commentItemState.isPendingReactionActionUpdatesInitiated);
    const pendingActions = pendingReactionActions ? pendingReactionActions.value : ([] as number[]);
    const reactionTypeToInfoMap = getCommentReactionInfoMap(state, artifactInfo, commentId, isPendingReactionActionUpdatesInitiated, pendingActions);

    return {
        isEditing: !!(commentItemState && commentItemState.isEditing),
        readonly,
        comment,
        isPendingReactionActionUpdatesInitiated,
        reactionTypeToInfoMap
    };
}

const Actions = {
    startEditItem: CommentsActions.startEditItem,
    cancelEditItem: CommentsActions.cancelEditItem,
    startDeleteItem: CommentsActions.startDeleteItem,
    startUpdateItem: CommentsActions.startUpdateItem,
    startGetItem: CommentsActions.startGetItem,
    startUpdatePendingReactions: CommentsActions.startUpdatePendingCommentReactions,
    startUpdateReaction: CommentsActions.startUpdateCommentReaction,
    startGetRecentlyReactedUsers: CommentsActions.startGetRecentlyReactedUsers
};

/**
 * Redux component of CommentItem.
 */
export const ConnectedCommentItem = connect(
    mapStateToProps,
    Actions
)(CommentItemAdapter);

VssComponent.register("connected-comment-item", ConnectedCommentItem as ((props: IProps) => JSX.Element));

/**
 * Redux component of CommentItem with dynamic module loader, which will
 * automatically attach itself to the default store in the context object.
 */
export class DynamicCommentItem extends VssComponent<IConnectedCommentItemProps> {
    public render(): JSX.Element {
        return (
            <DynamicModuleLoader modules={[CommentsReduxModule]}>
                <ConnectedCommentItem {...this.props} />
            </DynamicModuleLoader>
        );
    }
}

VssComponent.register("dynamic-comment-item", DynamicCommentItem);
